import { useState, useEffect } from 'react';
import { useTheme } from '../../styles/ThemeContext';
import { apiFetch } from '../../utils/api';
import { fmtEur } from '../../utils/helpers';
import { FiFileText, FiX, FiArrowLeft, FiEye, FiPrinter } from 'react-icons/fi';

/**
 * My Orders Panel — Shows the current user's own orders (read-only)
 * Drafts can be resumed via onResumeDraft
 */
export function MyOrdersPanel({ isOpen, onClose, username, onResumeDraft }) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch user's orders when panel opens
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setSelectedOrder(null);
      setOrderDetail(null);
      apiFetch('/api/orders/my')
        .then(r => r.ok ? r.json() : [])
        .then(data => setOrders(Array.isArray(data) ? data : []))
        .catch(() => setOrders([]))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const loadDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await apiFetch('/api/orders/' + id);
      if (res.ok) {
        const data = await res.json();
        setOrderDetail(data);
        setSelectedOrder(id);
      }
    } catch (e) {
      console.error('Failed to load order:', e);
    }
    setDetailLoading(false);
  };

  const backToList = () => {
    setSelectedOrder(null);
    setOrderDetail(null);
  };

  const confirmDelete = async () => {
    const o = deleteConfirm;
    if (!o) return;
    setDeleteConfirm(null);
    try {
      const res = await apiFetch('/api/orders/' + o.id, { method: 'DELETE' });
      if (res.ok) {
        setOrders(prev => prev.filter(x => x.id !== o.id));
      }
    } catch (e) {
      console.error('Failed to delete order:', e);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: c.overlay, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: c.bg, border: '1px solid ' + c.border, width: '100%', maxWidth: 1100, height: '90vh', display: 'flex', flexDirection: 'column', borderRadius: 6, overflow: 'hidden', boxShadow: '0 20px 60px ' + c.shadow }}
      >
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + c.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: c.bgCard, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: c.infoBg, border: '1px solid ' + c.infoBorder, borderRadius: 4, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiFileText size={15} color={c.info} />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: c.text }}>My Orders</div>
              <div style={{ fontSize: 10, color: c.textSecondary, fontFamily: 'monospace', letterSpacing: 0.8 }}>
                {username ? username.toUpperCase() + ' — ' : ''}PACKING LIST HISTORY
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ ...theme.btnG, padding: '5px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30 }}>
            <FiX size={14} />
          </button>
        </div>

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div onClick={() => setDeleteConfirm(null)} style={{ position: 'absolute', inset: 0, background: c.overlay, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: c.bgCard, border: '1px solid ' + c.border, borderRadius: 6, padding: 24, maxWidth: 400, width: '100%', boxShadow: '0 16px 32px ' + c.shadow }}>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12, color: c.text }}>Delete draft</div>
              <p style={{ margin: '0 0 20px 0', fontSize: 13, lineHeight: 1.6, color: c.textSecondary }}>
                Are you sure you want to delete draft <strong style={{ color: c.text, fontFamily: 'monospace' }}>{deleteConfirm.order_number}</strong>? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setDeleteConfirm(null)} style={{ ...theme.btnG, padding: '7px 16px', fontSize: 12 }}>Cancel</button>
                <button onClick={confirmDelete} style={{ background: c.error, color: '#fff', border: 'none', borderRadius: 4, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: c.textSecondary }}>Loading orders...</div>
          ) : selectedOrder && orderDetail ? (
            // ── Detail View ──
            <DetailView
              d={orderDetail}
              theme={theme}
              c={c}
              onBack={backToList}
              onResumeDraft={onResumeDraft}
              onClose={onClose}
            />
          ) : (
            // ── List View ──
            <ListView
              orders={orders}
              theme={theme}
              c={c}
              onSelect={loadDetail}
              onResumeDraft={onResumeDraft}
              onClose={onClose}
              detailLoading={detailLoading}
              onDeleteDraft={setDeleteConfirm}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Status badge helper ──
function statusBadge(status, c) {
  const s = (status || 'draft').toLowerCase();
  const styles = {
    draft:     { background: '#78350f22', color: '#f59e0b', border: '1px solid #f59e0b55' },
    confirmed: { background: '#05603a22', color: '#10b981', border: '1px solid #10b98155' },
    handled:   { background: '#0c4a6e22', color: '#38bdf8', border: '1px solid #38bdf855' },
  };
  const st = styles[s] || { background: '#ffffff11', color: c.textSecondary, border: '1px solid ' + c.border };
  return (
    <span style={{ fontSize: 9, fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.5, padding: '2px 7px', borderRadius: 3, textTransform: 'uppercase', ...st }}>
      {s}
    </span>
  );
}

// ── List View ──
function ListView({ orders, theme, c, onSelect, onResumeDraft, onClose, detailLoading, onDeleteDraft }) {
  const handleResume = async (e, orderId) => {
    e.stopPropagation();
    if (!onResumeDraft) return;
    try {
      const res = await apiFetch('/api/orders/' + orderId);
      if (res.ok) {
        const data = await res.json();
        onResumeDraft(data);
        onClose?.();
      }
    } catch (err) {
      console.error('Failed to load draft:', err);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 11, color: c.textSecondary, marginBottom: 12, fontFamily: 'monospace' }}>
        {orders.length} order{orders.length !== 1 ? 's' : ''} found
      </div>

      {orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: c.textSecondary, background: c.bgCard, border: '1px solid ' + c.border, borderRadius: 4 }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>📋</div>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>No orders yet</div>
          <div style={{ fontSize: 12 }}>Orders you create will appear here.</div>
        </div>
      ) : (
        <div style={{ border: '1px solid ' + c.border, borderRadius: 4, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Order #', 'Project', 'Dept', 'Units', 'Products', 'Value', 'DG', 'Status', 'Date', ''].map(h => (
                  <th key={h} style={theme.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr
                  key={o.id}
                  style={{ background: i % 2 ? c.bg : 'transparent', cursor: o.status !== 'draft' ? 'pointer' : 'default' }}
                  onClick={() => o.status !== 'draft' && !detailLoading && onSelect(o.id)}
                  onMouseEnter={(e) => { if (o.status !== 'draft') e.currentTarget.style.background = c.accentBg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 ? c.bg : 'transparent'; }}
                >
                  <td style={{ ...theme.td, fontWeight: 600, color: c.info, fontFamily: 'monospace' }}>{o.order_number}</td>
                  <td style={{ ...theme.td, fontSize: 11 }}>{o.project_number || '—'}</td>
                  <td style={{ ...theme.td, fontSize: 11 }}>{o.dept_number || '—'}</td>
                  <td style={{ ...theme.td, fontFamily: 'monospace', textAlign: 'center' }}>{o.total_units}</td>
                  <td style={{ ...theme.td, fontFamily: 'monospace', textAlign: 'center' }}>{o.total_products}</td>
                  <td style={{ ...theme.td, fontFamily: 'monospace' }}>€{fmtEur(o.total_value)}</td>
                  <td style={theme.td}>
                    {o.has_dg && <span style={{ fontSize: 9, background: c.errorBg, color: c.error, padding: '2px 5px', borderRadius: 2, fontFamily: 'monospace' }}>DG</span>}
                  </td>
                  <td style={theme.td}>{statusBadge(o.status, c)}</td>
                  <td style={{ ...theme.td, fontSize: 11, color: c.textSecondary }}>{o.created_at ? new Date(o.created_at).toLocaleDateString('en-GB') : '—'}</td>
                  <td style={{ ...theme.td, whiteSpace: 'nowrap' }}>
                    {o.status === 'draft' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        {onResumeDraft && (
                          <button
                            onClick={(e) => handleResume(e, o.id)}
                            style={{ background: c.accent, color: '#000', border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                          >
                            ▶ Resume
                          </button>
                        )}
                        {onDeleteDraft && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onDeleteDraft(o); }}
                            style={{ background: c.errorBg, color: c.error, border: '1px solid ' + c.errorBorder, borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Detail View ──
function DetailView({ d, theme, c, onBack, onResumeDraft, onClose }) {
  const [showPreview, setShowPreview] = useState(false);
  const units = d.units || [];
  const prods = d.products || [];
  const totalUnits = units.length;
  const totalProducts = prods.length;
  const totalQty = prods.reduce((s, p) => s + (Number(p.qty) || 0), 0);
  const totalValue = prods.reduce((s, p) => s + (Number(p.itemPrice) || 0) * (Number(p.qty) || 0), 0);
  const hasDg = prods.some(p => p.dg === 'Yes');
  const a = d.addresses || {};

  const handlePrint = () => {
    const el = document.getElementById('my-orders-print-preview');
    if (!el) return;
    const win = window.open('', '_blank');
    win.document.write('<html><head><title>' + d.order_number + '</title><style>body{margin:0;padding:20px;font-family:"Segoe UI",sans-serif}table{border-collapse:collapse;width:100%}@media print{body{padding:10px}}</style></head><body>');
    win.document.write(el.innerHTML);
    win.document.write('</body></html>');
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  if (showPreview) {
    return (
      <div>
        {/* Top bar */}
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <button onClick={() => setShowPreview(false)} style={{ ...theme.btnG, padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
            <FiArrowLeft size={13} /> Back to details
          </button>
          <button onClick={handlePrint} style={{ ...theme.btnG, padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, borderColor: c.accent, color: c.accent }}>
            <FiPrinter size={13} /> Print / PDF
          </button>
        </div>

        {/* Print preview - same layout as Step4Preview */}
        <div id="my-orders-print-preview" style={{ background: '#fff', color: '#111', padding: '32px 40px', borderRadius: 4, fontFamily: "'Segoe UI',sans-serif", maxWidth: 860, margin: '0 auto' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #111', paddingBottom: 16, marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5 }}>PACKING LIST</div>
              <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Transport Management System — GPS Activities</div>
              <div style={{ marginTop: 6, fontFamily: 'monospace', fontSize: 10, color: '#888' }}>Ref: <b style={{ color: '#111' }}>{d.order_number}</b></div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#888', fontFamily: 'monospace' }}>DATE</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB') : ''}</div>
              {a.preparedBy && (
                <>
                  <div style={{ fontSize: 10, color: '#888', fontFamily: 'monospace', marginTop: 5 }}>PREPARED BY</div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{a.preparedBy}</div>
                </>
              )}
            </div>
          </div>

          {/* Project Information */}
          {(d.dept_number || d.project_number) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: 1.5, color: '#d48a00', borderBottom: '1px solid #e0e0e0', paddingBottom: 4, marginBottom: 8 }}>Project Information</div>
              <div style={{ display: 'flex', gap: 24, fontFamily: 'monospace', fontSize: 11 }}>
                {d.dept_number && <div><span style={{ color: '#999' }}>DEPT# </span>{d.dept_number}</div>}
                {d.project_number && <div><span style={{ color: '#999' }}>PROJECT# </span>{d.project_number}</div>}
                {a.pickupDate && <div><span style={{ color: '#999' }}>PICKUP </span>{a.pickupDate}</div>}
                {a.deliveryDate && <div><span style={{ color: '#999' }}>DELIVERY </span>{a.deliveryDate}</div>}
              </div>
            </div>
          )}

          {/* Addresses */}
          {(a.collCompany || a.delCompany) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: 1.5, color: '#d48a00', borderBottom: '1px solid #e0e0e0', paddingBottom: 4, marginBottom: 8 }}>Addresses</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {[
                  ['Collection', [a.collCompany, a.collAddress, `${a.collPostal || ''} ${a.collCity || ''}`.trim(), a.collCountry, a.collContact, a.collPhone, a.collEmail]],
                  ['Delivery', [a.delCompany, a.delAddress, `${a.delPostal || ''} ${a.delCity || ''}`.trim(), a.delCountry, a.delContact, a.delPhone, a.delEmail]]
                ].map(([t, lines]) => (
                  <div key={t}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{t}</div>
                    <div style={{ fontSize: 12, lineHeight: 1.7 }}>{lines.filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shipping Units & Products */}
          <div style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: 1.5, color: '#d48a00', borderBottom: '1px solid #e0e0e0', paddingBottom: 4, marginBottom: 12 }}>Shipping Units & Products</div>
          {units.map((unit, ui) => {
            const up = prods.filter(p => p.unitId === unit.id);
            return (
              <div key={unit.id || ui} style={{ marginBottom: 16 }}>
                <div style={{ background: '#f4f4f4', padding: '7px 8px', fontWeight: 600, fontSize: 11, fontFamily: 'monospace', borderBottom: '1px solid #ddd', display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ color: '#d48a00' }}>#{String(ui + 1).padStart(2, '0')}</span>
                  <span>{unit.type} — Unit {unit.number}</span>
                  <span style={{ color: '#888', fontWeight: 400, marginLeft: 'auto', fontSize: 10 }}>
                    {unit.length}×{unit.width}×{unit.height} cm · Tare: {unit.tare}kg{unit.totalWeight ? ` · Total: ${unit.totalWeight}kg` : ''}
                    <span style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 2, background: '#eee', color: '#333', fontSize: 9, fontFamily: 'monospace' }}>{unit.export_type || 'Temporary'}</span>
                  </span>
                </div>
                {up.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#111' }}>
                        {['#', 'Category', 'Product', 'EQ #', 'Qty', 'CoO', 'HS Code', 'Unit €', 'Total €', 'DG'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '5px 7px', color: '#fff', fontSize: 9, fontFamily: 'monospace', letterSpacing: 0.5 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {up.map((p, i) => (
                        <tr key={p.id || i} style={{ borderBottom: '1px solid #eee', background: i % 2 ? '#fafafa' : '#fff' }}>
                          <td style={{ padding: '5px 7px', fontFamily: 'monospace', color: '#999', fontSize: 10 }}>{String(i + 1).padStart(2, '0')}</td>
                          <td style={{ padding: '5px 7px', color: '#666' }}>{p.category}</td>
                          <td style={{ padding: '5px 7px', fontWeight: 500 }}>{p.product}</td>
                          <td style={{ padding: '5px 7px', fontFamily: 'monospace' }}>{p.eqNumber || ''}</td>
                          <td style={{ padding: '5px 7px', fontFamily: 'monospace', textAlign: 'center' }}>{p.qty}</td>
                          <td style={{ padding: '5px 7px', fontFamily: 'monospace' }}>{p.coo}</td>
                          <td style={{ padding: '5px 7px', fontFamily: 'monospace' }}>{p.hsCode}</td>
                          <td style={{ padding: '5px 7px', fontFamily: 'monospace' }}>€{fmtEur(p.itemPrice)}</td>
                          <td style={{ padding: '5px 7px', fontFamily: 'monospace', fontWeight: 600 }}>€{fmtEur(p.itemPrice * p.qty)}</td>
                          <td style={{ padding: '5px 7px' }}>{p.dg === 'Yes' && <span style={{ color: '#c0392b', fontWeight: 700, fontSize: 9, fontFamily: 'monospace' }}>DG</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: 10, background: '#f9f9f9', color: '#999', fontSize: 11, border: '1px solid #ddd', borderTop: 'none', fontFamily: 'monospace' }}>No products in this unit</div>
                )}
              </div>
            );
          })}

          {/* Summary */}
          <div style={{ background: '#111', color: '#fff', padding: '14px 18px', marginTop: 16, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            {[['Total Units', totalUnits], ['Products', totalProducts], ['Total Qty', totalQty], ['Total Value', '€' + fmtEur(totalValue)], ['DG Items', prods.filter(p => p.dg === 'Yes').length]].map(([l, v]) => (
              <div key={l}>
                <div style={{ fontSize: 9, color: '#777', fontFamily: 'monospace', letterSpacing: 1 }}>{l}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: l === 'DG Items' && hasDg ? '#f85149' : '#f0a500', fontFamily: 'monospace' }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16, fontSize: 10, color: '#bbb', textAlign: 'center', fontFamily: 'monospace' }}>
            Generated by TMS Packing List Generator · {d.order_number}
          </div>
        </div>
      </div>
    );
  }

  const isDraft = d.status === 'draft';

  return (
    <div>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button onClick={onBack} style={{ ...theme.btnG, padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
          <FiArrowLeft size={13} /> Back to list
        </button>
        <button onClick={() => setShowPreview(true)} style={{ ...theme.btnG, padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, borderColor: c.accent, color: c.accent }}>
          <FiEye size={13} /> Preview
        </button>
        {isDraft && onResumeDraft && (
          <button
            onClick={() => { onResumeDraft(d); onClose?.(); }}
            style={{ ...theme.btnP, padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}
          >
            Resume draft
          </button>
        )}
      </div>

      {/* Order number + meta */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: c.accent }}>{d.order_number}</span>
        {statusBadge(d.status, c)}
        <span style={{ fontSize: 11, color: c.textSecondary, background: c.bgInput, padding: '2px 8px', borderRadius: 3 }}>Project: <strong style={{ color: c.text }}>{d.project_number || '—'}</strong></span>
        <span style={{ fontSize: 11, color: c.textSecondary, background: c.bgInput, padding: '2px 8px', borderRadius: 3 }}>Dept: <strong style={{ color: c.text }}>{d.dept_number || '—'}</strong></span>
        <span style={{ fontSize: 11, color: c.textSecondary }}>{d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB') : ''}</span>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        {[['Units', totalUnits], ['Products', totalProducts], ['Qty', totalQty], ['Value', '€' + fmtEur(totalValue)], ['DG', hasDg ? 'Yes' : 'No']].map(([l, v]) => (
          <div key={l} style={{ background: c.bgInput, padding: 10, borderRadius: 4, textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: c.textSecondary, fontFamily: 'monospace', letterSpacing: 1 }}>{l}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: l === 'DG' && hasDg ? c.error : c.accent, fontFamily: 'monospace' }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Addresses */}
      {d.addresses && (
        <div style={{ background: c.bgInput, border: '1px solid ' + c.border, borderRadius: 4, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 9, color: c.accent, letterSpacing: 1, marginBottom: 10, fontFamily: 'monospace' }}>ADDRESSES</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              ['Collection', a.collCompany, a.collAddress, [a.collPostal, a.collCity].filter(Boolean).join(' '), a.collCountry, a.collContact, a.collPhone, a.collEmail],
              ['Delivery', a.delCompany, a.delAddress, [a.delPostal, a.delCity].filter(Boolean).join(' '), a.delCountry, a.delContact, a.delPhone, a.delEmail]
            ].map(([t, ...lines]) => (
              <div key={t}>
                <div style={{ fontSize: 10, fontWeight: 700, color: c.accent, marginBottom: 4 }}>{t}</div>
                <div style={{ fontSize: 12, lineHeight: 1.7 }}>{lines.filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Units + Products */}
      {units.map((unit, ui) => {
        const up = prods.filter(p => p.unitId === unit.id);
        return (
          <div key={unit.id || ui} style={{ marginBottom: 12 }}>
            {/* Unit header */}
            <div style={{ background: c.bgInput, padding: '8px 12px', fontWeight: 600, fontSize: 11, fontFamily: 'monospace', borderBottom: '1px solid ' + c.border, display: 'flex', gap: 10, alignItems: 'center', borderRadius: '4px 4px 0 0' }}>
              <span style={{ color: c.accent }}>#{String(ui + 1).padStart(2, '0')}</span>
              <span>{unit.type} — Unit {unit.number}</span>
              <span style={{ color: c.textSecondary, fontWeight: 400, marginLeft: 'auto', fontSize: 10 }}>
                {unit.length}x{unit.width}x{unit.height} cm · Tare: {unit.tare}kg
                <span style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 2, background: c.bg, color: c.accent, fontSize: 9, fontFamily: 'monospace' }}>{unit.export_type || 'Temporary'}</span>
              </span>
            </div>

            {/* Product table */}
            {up.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid ' + c.border, borderTop: 'none' }}>
                <thead>
                  <tr style={{ background: c.bgCard }}>
                    {['#', 'Product', 'EQ', 'Qty', 'CoO', 'HS', 'Price', 'DG'].map(h => (
                      <th key={h} style={{ ...theme.th, fontSize: 8 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {up.map((p, i) => (
                    <tr key={p.id || i} style={{ borderBottom: '1px solid ' + c.border }}>
                      <td style={{ ...theme.td, fontFamily: 'monospace', fontSize: 10, color: c.textSecondary }}>{String(i + 1).padStart(2, '0')}</td>
                      <td style={{ ...theme.td, fontWeight: 500 }}>{p.product}</td>
                      <td style={{ ...theme.td, fontFamily: 'monospace', fontSize: 10 }}>{p.eqNumber || '—'}</td>
                      <td style={{ ...theme.td, fontFamily: 'monospace', textAlign: 'center' }}>{p.qty}</td>
                      <td style={{ ...theme.td, fontFamily: 'monospace', fontSize: 10 }}>{p.coo || '—'}</td>
                      <td style={{ ...theme.td, fontFamily: 'monospace', fontSize: 10 }}>{p.hsCode || '—'}</td>
                      <td style={{ ...theme.td, fontFamily: 'monospace', fontSize: 10 }}>€{fmtEur(p.itemPrice * p.qty)}</td>
                      <td style={theme.td}>{p.dg === 'Yes' && <span style={{ fontSize: 9, background: c.errorBg, color: c.error, padding: '2px 5px', borderRadius: 2, fontFamily: 'monospace' }}>DG</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ background: c.bgCard, border: '1px solid ' + c.border, borderTop: 'none', padding: 10, textAlign: 'center', color: c.textSecondary, fontSize: 11 }}>No products</div>
            )}
          </div>
        );
      })}

    </div>
  );
}
